import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FileText, Plus, Search, ArrowLeft, Upload, Download, Trash2, Edit,
  Calculator, Check, AlertCircle, Clock, Send, CheckCircle, XCircle,
  ChevronRight, Building2, User, Calendar, Hash, FileSignature,
  Loader2, Eye, RefreshCw, Scale, Briefcase, ArrowRight, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { extrajudicialAPI, contactosAPI } from '../api';
import { toast } from '../hooks/use-toast';

// Configuración de estados y colores
const ESTADOS_CONFIG = {
  borrador: { nombre: 'Borrador', color: 'bg-gray-100 text-gray-700', icon: Edit },
  pendiente_firma: { nombre: 'Pendiente Firma', color: 'bg-yellow-100 text-yellow-700', icon: FileSignature },
  enviado_banco: { nombre: 'Enviado al Banco', color: 'bg-blue-100 text-blue-700', icon: Send },
  pendiente_respuesta: { nombre: 'Pendiente Respuesta', color: 'bg-orange-100 text-orange-700', icon: Clock },
  finalizado: { nombre: 'Finalizado', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  convertido_expediente: { nombre: 'Convertido a Expediente', color: 'bg-purple-100 text-purple-700', icon: Briefcase },
};

const RESULTADOS_CONFIG = {
  pendiente: { nombre: 'Pendiente', color: 'bg-gray-100 text-gray-700' },
  estimada: { nombre: 'Estimada', color: 'bg-green-100 text-green-700' },
  desestimada: { nombre: 'Desestimada', color: 'bg-red-100 text-red-700' },
  parcial: { nombre: 'Parcialmente Estimada', color: 'bg-yellow-100 text-yellow-700' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-ES');
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount);
};

// =============================================================================
// COMPONENTE PRINCIPAL: Lista de Reclamaciones
// =============================================================================
const ReclamacionExtrajudicial = () => {
  const navigate = useNavigate();
  const [reclamaciones, setReclamaciones] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, [filterEstado]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = { search: search || undefined, estado: filterEstado || undefined };
      const [recRes, statsRes] = await Promise.all([
        extrajudicialAPI.getAll(params),
        extrajudicialAPI.getStats()
      ]);
      setReclamaciones(recRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar las reclamaciones', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadData();
  };

  const handleEliminar = async (e, id, referencia) => {
    e.stopPropagation(); // Evitar navegación al hacer clic
    if (!window.confirm(`¿Eliminar la reclamación ${referencia}?\n\nEsta acción no se puede deshacer.`)) return;
    
    try {
      await extrajudicialAPI.delete(id);
      toast({ title: 'Eliminada', description: `Reclamación ${referencia} eliminada correctamente` });
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo eliminar la reclamación', variant: 'destructive' });
    }
  };

  const getEstadoInfo = (estado) => ESTADOS_CONFIG[estado] || ESTADOS_CONFIG.borrador;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Scale className="w-8 h-8 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reclamación Extrajudicial</h1>
            <p className="text-gray-600">Gestión de reclamaciones bancarias previas a vía judicial</p>
          </div>
        </div>
        <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setIsNewDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Nueva Reclamación
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {Object.entries(ESTADOS_CONFIG).map(([key, config]) => {
          const Icon = config.icon;
          const count = stats.por_estado?.[key] || 0;
          return (
            <Card 
              key={key} 
              className={`cursor-pointer transition-all hover:shadow-md ${filterEstado === key ? 'ring-2 ring-indigo-500' : ''}`}
              onClick={() => setFilterEstado(filterEstado === key ? '' : key)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-4 h-4 text-gray-500" />
                  <span className="text-xs font-medium text-gray-600 truncate">{config.nombre}</span>
                </div>
                <p className="text-2xl font-bold">{count}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[250px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar por referencia, cliente o banco..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
            </div>
            <Select value={filterEstado || 'all'} onValueChange={(v) => setFilterEstado(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                {Object.entries(ESTADOS_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handleSearch}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Buscar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : reclamaciones.length > 0 ? (
            <div className="divide-y">
              {reclamaciones.map((rec) => {
                const estadoInfo = getEstadoInfo(rec.estado);
                const EstadoIcon = estadoInfo.icon;
                return (
                  <div 
                    key={rec.id}
                    className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/extrajudicial/${rec.id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono font-bold text-indigo-600">{rec.numero_referencia}</span>
                          <Badge className={estadoInfo.color}>
                            <EstadoIcon className="w-3 h-3 mr-1" />
                            {estadoInfo.nombre}
                          </Badge>
                          {rec.resultado && rec.resultado !== 'pendiente' && (
                            <Badge className={RESULTADOS_CONFIG[rec.resultado]?.color}>
                              {RESULTADOS_CONFIG[rec.resultado]?.nombre}
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {rec.cliente_nombre || 'Sin cliente'}
                          </span>
                          {rec.entidad_bancaria_actual && (
                            <span className="flex items-center gap-1">
                              <Building2 className="w-4 h-4" />
                              {rec.entidad_bancaria_actual}
                            </span>
                          )}
                          {rec.fecha_firma_hipoteca && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(rec.fecha_firma_hipoteca)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-green-600">
                          {formatCurrency(rec.total_reclamacion)}
                        </p>
                        <p className="text-xs text-gray-500">{formatDate(rec.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={(e) => handleEliminar(e, rec.id, rec.numero_referencia)}
                          title="Eliminar reclamación"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-12 text-center text-gray-500">
              <Scale className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No hay reclamaciones extrajudiciales</p>
              <Button variant="outline" className="mt-4" onClick={() => setIsNewDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Crear primera reclamación
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Nueva Reclamación */}
      <NuevaReclamacionDialog 
        isOpen={isNewDialogOpen}
        onClose={() => setIsNewDialogOpen(false)}
        onSuccess={(id) => {
          setIsNewDialogOpen(false);
          navigate(`/extrajudicial/${id}`);
        }}
      />
    </div>
  );
};

// =============================================================================
// DIALOG: Nueva Reclamación (Seleccionar Cliente)
// =============================================================================
const NuevaReclamacionDialog = ({ isOpen, onClose, onSuccess }) => {
  const [clientes, setClientes] = useState([]);
  const [searchCliente, setSearchCliente] = useState('');
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isCreatingCliente, setIsCreatingCliente] = useState(false);
  const [nuevoCliente, setNuevoCliente] = useState({
    nombre: '', nif: '', direccion: '', codigo_postal: '', ciudad: '', provincia: '', telefono: '', email: '', notas: ''
  });

  useEffect(() => {
    if (isOpen) {
      setSelectedCliente(null);
      setSearchCliente('');
      setIsCreatingCliente(false);
      setNuevoCliente({ nombre: '', nif: '', direccion: '', codigo_postal: '', ciudad: '', provincia: '', telefono: '', email: '', notas: '' });
    }
  }, [isOpen]);

  const handleSearchClientes = async () => {
    if (searchCliente.length < 2) return;
    setLoading(true);
    try {
      const res = await contactosAPI.getAll({ search: searchCliente, limit: 10 });
      const data = res.data?.data || res.data?.items || res.data || [];
      setClientes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error buscando clientes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCrearCliente = async () => {
    if (!nuevoCliente.nombre) {
      toast({ title: 'Error', description: 'El nombre es obligatorio', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const res = await contactosAPI.create({
        nombre: nuevoCliente.nombre,
        tipo: 'persona',
        nif_cif: nuevoCliente.nif,
        direccion: nuevoCliente.direccion,
        codigo_postal: nuevoCliente.codigo_postal,
        ciudad: nuevoCliente.ciudad,
        provincia: nuevoCliente.provincia,
        telefono: nuevoCliente.telefono,
        email: nuevoCliente.email,
        notas: nuevoCliente.notas
      });
      const clienteCreado = res.data;
      // Construir dirección completa para mostrar
      const direccionCompleta = [
        clienteCreado.direccion,
        clienteCreado.codigo_postal,
        clienteCreado.ciudad,
        clienteCreado.provincia
      ].filter(Boolean).join(', ');
      
      setSelectedCliente({
        id: clienteCreado.id,
        nombre: clienteCreado.nombre,
        nif: clienteCreado.nif_cif || nuevoCliente.nif,
        direccion: direccionCompleta || clienteCreado.direccion,
        telefono: clienteCreado.telefono,
        email: clienteCreado.email
      });
      setIsCreatingCliente(false);
      toast({ title: 'Cliente creado', description: `${clienteCreado.nombre} añadido a contactos` });
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo crear el cliente', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!selectedCliente) {
      toast({ title: 'Error', description: 'Selecciona un cliente', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const res = await extrajudicialAPI.create({
        cliente_id: selectedCliente.id,
        cliente_nombre: selectedCliente.nombre,
        cliente_dni: selectedCliente.nif,
        cliente_direccion: selectedCliente.direccion,
        cliente_telefono: selectedCliente.telefono,
        cliente_email: selectedCliente.email
      });
      toast({ title: 'Reclamación creada', description: `Referencia: ${res.data.numero_referencia}` });
      onSuccess(res.data.id);
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo crear la reclamación', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="w-5 h-5" />
            Nueva Reclamación Extrajudicial
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {!isCreatingCliente ? (
            <>
              <div>
                <Label>Buscar Cliente</Label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Nombre, DNI o email del cliente..."
                      className="pl-10"
                      value={searchCliente}
                      onChange={(e) => setSearchCliente(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearchClientes()}
                    />
                  </div>
                  <Button variant="outline" onClick={handleSearchClientes} disabled={loading}>
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buscar'}
                  </Button>
                </div>
              </div>

              {clientes.length > 0 && (
                <div className="max-h-48 overflow-auto border rounded-lg divide-y">
                  {clientes.map((cliente) => (
                    <div
                      key={cliente.id}
                      className={`p-3 cursor-pointer hover:bg-gray-50 ${selectedCliente?.id === cliente.id ? 'bg-indigo-50 border-l-4 border-indigo-500' : ''}`}
                      onClick={() => setSelectedCliente({
                        ...cliente,
                        nombre: cliente.nombre_completo || cliente.nombre
                      })}
                    >
                      <p className="font-medium">{cliente.nombre_completo || cliente.nombre}</p>
                      <p className="text-sm text-gray-500">
                        {cliente.nif_cif && `DNI: ${cliente.nif_cif}`}
                        {cliente.telefono && ` · Tel: ${cliente.telefono}`}
                        {cliente.email && ` · ${cliente.email}`}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {clientes.length === 0 && searchCliente.length >= 2 && !loading && (
                <div className="text-center py-4 border rounded-lg bg-gray-50">
                  <p className="text-gray-500 text-sm mb-2">No se encontraron clientes</p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setIsCreatingCliente(true);
                      setNuevoCliente({ ...nuevoCliente, nombre: searchCliente });
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Crear nuevo cliente
                  </Button>
                </div>
              )}

              {/* Botón para crear cliente siempre visible */}
              <div className="border-t pt-3">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  onClick={() => setIsCreatingCliente(true)}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Crear nuevo cliente
                </Button>
              </div>

              {selectedCliente && (
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">Cliente seleccionado</p>
                        <p className="text-sm text-green-700">{selectedCliente.nombre} - {selectedCliente.nif || selectedCliente.nif_cif}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            /* Formulario crear nuevo cliente - Completo como en Contactos */
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
              <div className="flex items-center justify-between sticky top-0 bg-white pb-2">
                <p className="font-medium text-gray-900">Nuevo Cliente</p>
                <Button variant="ghost" size="sm" onClick={() => setIsCreatingCliente(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Datos personales */}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs">Nombre completo *</Label>
                  <Input
                    value={nuevoCliente.nombre}
                    onChange={(e) => setNuevoCliente({...nuevoCliente, nombre: e.target.value})}
                    placeholder="Nombre y apellidos"
                  />
                </div>
                <div>
                  <Label className="text-xs">DNI/NIF</Label>
                  <Input
                    value={nuevoCliente.nif}
                    onChange={(e) => setNuevoCliente({...nuevoCliente, nif: e.target.value})}
                    placeholder="12345678A"
                  />
                </div>
                <div>
                  <Label className="text-xs">Teléfono</Label>
                  <Input
                    value={nuevoCliente.telefono}
                    onChange={(e) => setNuevoCliente({...nuevoCliente, telefono: e.target.value})}
                    placeholder="600 000 000"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Email</Label>
                  <Input
                    type="email"
                    value={nuevoCliente.email}
                    onChange={(e) => setNuevoCliente({...nuevoCliente, email: e.target.value})}
                    placeholder="cliente@email.com"
                  />
                </div>
              </div>

              {/* Dirección completa */}
              <div className="border-t pt-3 mt-3">
                <p className="text-xs font-medium text-gray-500 mb-2">DIRECCIÓN</p>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">Dirección (calle, número, piso...)</Label>
                    <Input
                      value={nuevoCliente.direccion}
                      onChange={(e) => setNuevoCliente({...nuevoCliente, direccion: e.target.value})}
                      placeholder="Calle Mayor, 1, 2º A"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs">Código Postal</Label>
                      <Input
                        value={nuevoCliente.codigo_postal}
                        onChange={(e) => setNuevoCliente({...nuevoCliente, codigo_postal: e.target.value})}
                        placeholder="08001"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Ciudad</Label>
                      <Input
                        value={nuevoCliente.ciudad}
                        onChange={(e) => setNuevoCliente({...nuevoCliente, ciudad: e.target.value})}
                        placeholder="Barcelona"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Provincia</Label>
                      <Input
                        value={nuevoCliente.provincia}
                        onChange={(e) => setNuevoCliente({...nuevoCliente, provincia: e.target.value})}
                        placeholder="Barcelona"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Notas */}
              <div className="border-t pt-3 mt-3">
                <Label className="text-xs">Notas</Label>
                <Textarea
                  value={nuevoCliente.notas}
                  onChange={(e) => setNuevoCliente({...nuevoCliente, notas: e.target.value})}
                  placeholder="Observaciones adicionales..."
                  rows={2}
                />
              </div>

              <Button 
                className="w-full mt-3" 
                onClick={handleCrearCliente}
                disabled={saving || !nuevoCliente.nombre}
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Crear Cliente y Seleccionar
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button 
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            onClick={handleCreate}
            disabled={!selectedCliente || saving || isCreatingCliente}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Crear Reclamación
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReclamacionExtrajudicial;
